// src/pages/outsourcing_management/constants/requisitionStatusColors.ts
//
// Status colours and display labels for the internal Requisition workflow
// (EE-207, realigned in EE-253) - RequisitionStatusEnum. Requisitions are
// grouped under Outsourcing (every one of them spawns a partner Inquiry), so
// this reuses the `StatusColors` shape and lives alongside
// inquiryStatusColors.ts rather than introducing a new pattern.
import { RequisitionStatusEnum } from "types/RequisitionTypes";
import {
  STATUS_PALETTE,
  StatusColors,
} from "components/Dashboard/StatusFilterChips";

export const REQUISITION_STATUS_COLORS: Record<
  RequisitionStatusEnum,
  StatusColors
> = {
  [RequisitionStatusEnum.DRAFT]: STATUS_PALETTE.DRAFT,
  [RequisitionStatusEnum.SUBMITTED]: STATUS_PALETTE.DRAFT,
  [RequisitionStatusEnum.APPROVED]: STATUS_PALETTE.APPROVED,
  [RequisitionStatusEnum.AWAITING_SUPPLIER]: {
    border: "1px solid #007474",
    color: "#007474",
    background: "#e6f1f1",
  },
  [RequisitionStatusEnum.ORDERED]: STATUS_PALETTE.ORDERED,
  [RequisitionStatusEnum.PICKUP_DELIVERY]: STATUS_PALETTE.PICKUP_DELIVERY,
  [RequisitionStatusEnum.READY_TO_USE]: STATUS_PALETTE.READY_TO_USE,
  [RequisitionStatusEnum.DONE]: STATUS_PALETTE.DONE,
  [RequisitionStatusEnum.CLOSED]: STATUS_PALETTE.CLOSED,
};

// What a status is called on screen. The enum values are shouty and two of
// them (PICKUP_DELIVERY, READY_TO_USE) are unreadable raw.
export const REQUISITION_STATUS_LABELS: Record<RequisitionStatusEnum, string> = {
  [RequisitionStatusEnum.DRAFT]: "Draft",
  [RequisitionStatusEnum.SUBMITTED]: "Submitted",
  [RequisitionStatusEnum.APPROVED]: "Approved",
  // EE-253: purchasing calls this stage WIP, so the screen does too. The
  // stored enum stays AWAITING_SUPPLIER - the value is an identifier, and
  // renaming it would mean migrating REQUISITION_HEADERS.status plus every
  // historic REQUISITION_AUDIT string for a word. The status emails say WIP
  // as well (_email_i18n.html.jinja2), so the requester reads one name.
  [RequisitionStatusEnum.AWAITING_SUPPLIER]: "WIP",
  [RequisitionStatusEnum.ORDERED]: "Ordered",
  [RequisitionStatusEnum.PICKUP_DELIVERY]: "Pickup / Delivery",
  [RequisitionStatusEnum.READY_TO_USE]: "Ready to Use",
  // Two terminal states, two different stories. "Done" is the requisition
  // that completed; "Closed" is the one that was cancelled or rejected out.
  [RequisitionStatusEnum.DONE]: "Done",
  [RequisitionStatusEnum.CLOSED]: "Closed",
};

// Falls back to the raw value so retired statuses still render - the audit
// trail replays from_status/to_status strings written before EE-253
// (IN_PROCESS, RECEIVED), which are no longer enum members.
export const requisitionStatusLabel = (status: string | null | undefined): string =>
  status
    ? REQUISITION_STATUS_LABELS[status as RequisitionStatusEnum] ?? status
    : "";

// Everything except the two terminal states, mirroring ACTIVE_STATUSES in
// backend/common/enums/RequisitionStatusEnum.py. Held here as the single
// definition rather than a literal at each call site, which is how the
// Inquiry equivalent silently drifted from its enum.
export const ACTIVE_REQUISITION_STATUSES: RequisitionStatusEnum[] =
  Object.values(RequisitionStatusEnum).filter(
    (status) =>
      status !== RequisitionStatusEnum.DONE &&
      status !== RequisitionStatusEnum.CLOSED
  );
